import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import PasswordField from "./PasswordField";

function pickRegisterErrorMessage(data, t) {
  if (!data) return t("authModal.register.errors.generic");
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return t("authModal.register.errors.generic");
}

function RegisterModalForm({ onSwitchToLogin, onRegisterSuccess }) {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState(null);

  const initialValues = {
    name: "",
    phone_number: "",
    email: "",
    password: "",
    password_confirmation: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t("authModal.register.errors.nameRequired")),
    phone_number: Yup.string()
      .matches(
        /^\d{6,}$/,
        t("authModal.register.errors.phoneInvalid"),
      )
      .required(t("authModal.register.errors.phoneRequired")),
    email: Yup.string()
      .email(t("authModal.register.errors.emailInvalid"))
      .required(t("authModal.register.errors.emailRequired")),
    password: Yup.string()
      .min(8, t("authModal.register.errors.passwordMin"))
      .matches(/[A-Za-z]/, t("authModal.register.errors.passwordLetter"))
      .required(t("authModal.register.errors.passwordRequired")),
    password_confirmation: Yup.string()
      .oneOf(
        [Yup.ref("password")],
        t("authModal.register.errors.passwordMismatch"),
      )
      .required(t("authModal.register.errors.passwordConfirmRequired")),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      name: values.name.trim(),
      phone_number: values.phone_number.trim(),
      email: values.email.trim(),
      password: values.password,
      password_confirmation: values.password_confirmation,
      nomio_partner: true,
    };
    const locale = getSecondApiLocale(i18n.language);

    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/register`,
        payload,
      );

      console.log(data);
      
      if (data?.isSuccess !== true) {
        setMessage({
          status: false,
          text: pickRegisterErrorMessage(data, t),
        });
        return;
      }

      const registeredEmail = payload.email;
      resetForm();
      if (typeof onRegisterSuccess === "function") {
        onRegisterSuccess(registeredEmail);
      } else {
        const okText =
          data && typeof data.message === "string" && data.message.trim()
            ? data.message
            : t("authModal.register.success");
        setMessage({ status: true, text: okText });
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || !error.response) {
        setMessage({
          status: false,
          text: t("authModal.register.errors.network"),
        });
      } else {
        setMessage({
          status: false,
          text: pickRegisterErrorMessage(error.response?.data, t),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <Form className="row gy-4">
          {message && (
            <div
              className={`col-12 font-14 ${
                message.status ? "text-success" : "text-danger"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.register.labels.name")}
            </label>
            <div className="position-relative">
              <Field
                type="text"
                name="name"
                autoComplete="name"
                className="common-input common-input--withIcon"
                placeholder={t("authModal.register.placeholders.name")}
              />
              <span className="input-icon">
                <img src="../assets/images/icons/user-icon.svg" alt="" />
              </span>
            </div>
            <ErrorMessage
              name="name"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.register.labels.phone_number")}
            </label>
            <div className="position-relative">
              <Field name="phone_number">
                {({ field, form }) => (
                  <input
                    {...field}
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="tel"
                    className="common-input common-input--withIcon"
                    placeholder={t("authModal.register.placeholders.phone_number")}
                    onChange={(event) => {
                      form.setFieldValue(
                        field.name,
                        event.target.value.replace(/\D/g, ""),
                      );
                    }}
                  />
                )}
              </Field>
              <span className="input-icon">
                <img src="../assets/images/icons/user-icon.svg" alt="" />
              </span>
            </div>
            <ErrorMessage
              name="phone_number"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.register.labels.email")}
            </label>
            <div className="position-relative">
              <Field
                type="email"
                name="email"
                autoComplete="email"
                className="common-input common-input--withIcon"
                placeholder={t("authModal.register.placeholders.email")}
              />
              <span className="input-icon">
                <img src="../assets/images/icons/envelope-icon.svg" alt="" />
              </span>
            </div>
            <ErrorMessage
              name="email"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.register.labels.password")}
            </label>
            <PasswordField name="password" autoComplete="new-password" />
            <ErrorMessage
              name="password"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.register.labels.password_confirmation")}
            </label>
            <PasswordField
              name="password_confirmation"
              autoComplete="new-password"
            />
            <ErrorMessage
              name="password_confirmation"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-main btn-lg w-100 pill"
            >
              {isSubmitting
                ? t("authModal.register.btnLoading")
                : t("authModal.register.btn")}
            </button>
          </div>

          {typeof onSwitchToLogin === "function" && (
            <div className="col-12">
              <p className="text font-14 mb-0 text-center">
                {t("authModal.register.haveAccount")}{" "}
                <button
                  type="button"
                  className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0"
                  onClick={onSwitchToLogin}
                >
                  {t("authModal.register.goLogin")}
                </button>
              </p>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
}

export default RegisterModalForm;
